import { SkupremeModal } from '@components/Modal';
import { HoverBorderButton, MuteText, StyledButton, StyledInput } from '@elements/index';
import { WaveRuleSearchInput } from '@page-components/warehouse/rule-search';
import { WaveService } from '@utility/services';
import { ToastMessage } from '@utility/toast';
import {
  type Dispatch,
  Fragment,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AllocationSelection } from './ChangeLocationPopup';
import styled from 'styled-components';
import { Divider } from 'semantic-ui-react';
import { ItemImage } from '@components/CatalogElements';
import { OrganisationAvatar } from '@components/ImageAvatar';
import { X } from '@phosphor-icons/react';

type FormValue = {
  waveNumber: string;
  waveRuleId?: {
    _id: string;
    name: string;
  };
};

type CreateAllocationWavePopupProps = {
  warehouseId: string;
  open: boolean;
  onClose: () => void;
  refresh: () => void;
  allocationSelection: AllocationSelection;
  setAllocationSelection: Dispatch<SetStateAction<AllocationSelection>>;
};

const AllocationItemContainer = styled.div`
  .title {
    font-size: 12px;
    max-width: 210px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cancel-icon {
    color: ${({ theme }) => theme.colors.grey};
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const AllocationRow = ({
  allocation,
  onRemove,
}: {
  allocation: AllocationSelection[keyof AllocationSelection];
  onRemove: (allocationId: string) => void;
}) => {
  return (
    <AllocationItemContainer className="row align-items-center">
      <div className="col-5 d-flex align-items-center">
        <ItemImage
          item={{
            pImages: {
              displayImage: allocation.image,
            },
          }}
          size="mini"
        />
        <div className="d-flex flex-column ml-2">
          <span className="title">{allocation.name}</span>
          <div>
            <MuteText>SKU:</MuteText>
            <span>{allocation.sku}</span>
          </div>
          <div className="d-flex align-items-center">
            <OrganisationAvatar
              name={allocation.customerName}
              image={allocation.customerImage}
              mini
            />
            <span className="ml-1 title">{allocation.customerName}</span>
          </div>
        </div>
      </div>
      <div className="col-5 ">
        <div>
          <MuteText>Rack:</MuteText>
          <span className="ml-1">{allocation.rack}</span>
        </div>
        <div>
          <MuteText>Location:</MuteText>
          <span className="ml-1">{allocation.location}</span>
        </div>
        <div>
          <MuteText>Order ID:</MuteText>
          <span className="ml-1">{allocation.orderId}</span>
        </div>
      </div>
      <div className="col-2 d-flex align-items-center justify-content-end">
        <div
          className="cursor-pointer cancel-icon"
          onClick={() => onRemove(allocation.id)}
        >
          <X size={20} />
        </div>
      </div>
    </AllocationItemContainer>
  );
};

const AllocationsContainer = styled.div`
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  border-radius: 5px;
  margin: 10px 0;
  max-height: 40vh;
  overflow-y: auto;
`;

export function CreateAllocationWavePopup({
  warehouseId,
  open,
  onClose,
  refresh,
  allocationSelection,
  setAllocationSelection,
}: CreateAllocationWavePopupProps) {
  const [waveNumberLoading, setWaveNumberLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formValue, setFormValues] = useState<FormValue>({
    waveNumber: '',
  });

  const setValue = (key, value) => {
    setFormValues({
      ...formValue,
      [key]: value,
    });
  };

  const getNewWaveNumber = async () => {
    try {
      setWaveNumberLoading(true);
      const res = await WaveService.generateWaveNumber({ warehouseId });
      setValue('waveNumber', res?.waveNumber);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setWaveNumberLoading(false);
    }
  };

  useEffect(() => {
    if (warehouseId && open) {
      getNewWaveNumber();
    } else {
      setFormValues({
        waveNumber: '',
        waveRuleId: undefined,
      });
    }
  }, [warehouseId, open]);

  const createWave = async () => {
    if (!formValue.waveRuleId._id) {
      ToastMessage.error('Please select a wave rule');
      return;
    }

    if (!formValue.waveNumber) {
      ToastMessage.error('Please enter a wave number');
      return;
    }

    if (selectedAllocations.length === 0) {
      ToastMessage.error('Please select at least one allocation');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        warehouseId,
        waveNumber: formValue.waveNumber,
        waveRuleId: formValue.waveRuleId._id,
        allocationIds: selectedAllocations.map(allocation => allocation.id),
      };
      await WaveService.createAllocationWave(payload);
      ToastMessage.success('Wave created successfully');
      onClose();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const selectedAllocations = useMemo(() => {
    return Object.values(allocationSelection).filter(x => x.selected);
  }, [allocationSelection]);

  const removeAllocation = (allocationId: string) => {
    setAllocationSelection(prev => {
      const newSelection = { ...prev };
      delete newSelection[allocationId];
      return newSelection;
    });
  };

  return (
    <SkupremeModal
      open={open}
      title={`${selectedAllocations.length} allocations - Create a Manual Wave`}
      size="small"
      onClose={onClose}
    >
      <div className="d-flex flex-column">
        <MuteText>Wave Number</MuteText>
        <StyledInput
          value={formValue.waveNumber}
          onChange={e => setValue('waveNumber', e.target.value)}
          placeholder="Wave Number"
          loading={waveNumberLoading}
        />
      </div>
      <div className="d-flex flex-column mt-2 mb-4">
        <MuteText>Wave Rule</MuteText>
        <WaveRuleSearchInput
          onResultClick={rule => setValue('waveRuleId', rule)}
          warehouseId={warehouseId}
          top={undefined}
          inputClassName={undefined}
        />
        {formValue?.waveRuleId?.name && (
          <span>
            {formValue?.waveRuleId?.name}
            <MuteText> - Selected</MuteText>
          </span>
        )}
      </div>
      <AllocationsContainer className="d-flex flex-column">
        {selectedAllocations?.map((allocation, index) => (
          <Fragment key={allocation.id}>
            <AllocationRow allocation={allocation} onRemove={removeAllocation} />
            {index !== selectedAllocations?.length - 1 && <Divider className="my-1" />}
          </Fragment>
        ))}
      </AllocationsContainer>
      <div className="d-flex justify-content-end">
        <HoverBorderButton onClick={onClose} disabled={loading}>
          Cancel
        </HoverBorderButton>
        <StyledButton loading={loading} className="ml-2" onClick={createWave}>
          Create
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}
